import { buildELKLayout } from '../../utils/elkLayout'

export const DIFFICULTY_BADGE = {
  E: { bg: '#DCFCE7', text: '#166534' },
  M: { bg: '#FEF3C7', text: '#92400E' },
  H: { bg: '#FEE2E2', text: '#991B1B' }
}

export const DIFFICULTY_LABELS = {
  E: 'Easy',
  M: 'Medium',
  H: 'Hard'
}

export const STATUS_COLORS = {
  UNVISITED: { bg: 'rgba(156,163,175,0.1)', border: '#9CA3AF', dot: '#9CA3AF', text: 'Unvisited' },
  STRUGGLING: { bg: 'rgba(239,68,68,0.1)', border: '#EF4444', dot: '#EF4444', text: 'Struggling' },
  IN_PROGRESS: {
    bg: 'rgba(234,179,8,0.1)',
    border: '#EAB308',
    dot: '#EAB308',
    text: 'In Progress'
  },
  MASTERED: { bg: 'rgba(34,197,94,0.1)', border: '#22C55E', dot: '#22C55E', text: 'Mastered' }
}

const MASTERY_THRESHOLD = 0.7
const SCORE_DELTAS = { E: 0.1, M: 0.15, H: 0.25 }

/**
 * Calculate remaining correct answers to reach mastery, and a formatted label.
 * Returns { remaining, label } where remaining is 0 for mastered/unvisited nodes.
 */
export function getRemainingToMaster(score, difficulty, status) {
  if (status === 'MASTERED') return { remaining: 0, label: 'Mastered!' }
  if (status === 'UNVISITED' || !score) return { remaining: 0, label: '' }

  const delta = SCORE_DELTAS[difficulty] || 0.15
  const remaining = Math.ceil((MASTERY_THRESHOLD - score) / delta)
  if (remaining <= 0) return { remaining: 0, label: 'Mastered!' }
  return {
    remaining,
    label: `${remaining} more correct to master`
  }
}

/**
 * Mastery score as a percentage of the 0.7 threshold, capped at 100.
 */
export function getMasteryPercent(score) {
  return Math.min((score || 0) / MASTERY_THRESHOLD, 1) * 100
}

export { MASTERY_THRESHOLD, SCORE_DELTAS }

export const NODE_WIDTH = 240
export const NODE_HEIGHT = 80
// Skill nodes carry the extra DOK strip row, so reserve more layout height.
export const SKILL_NODE_HEIGHT = 104

// DOK (Depth of Knowledge) is 1:1 with difficulty by construction at ingestion
// (server DOK_DIFFICULTY_MAP: DOK 1↔E, DOK 2↔M, DOK 3↔H), so the client derives it locally.
export const DOK_BY_DIFFICULTY = { E: 'DOK 1', M: 'DOK 2', H: 'DOK 3' }
const DIFFICULTY_ORDER = ['E', 'M', 'H']

/** Skill-level identity for a node: the skillGroupKey minus the difficulty dimension. */
export function skillKeyOf(node) {
  return `${node.testType || node.test_type}:${node.domain}:${node.skill}`
}

/**
 * Collapse skill×difficulty graph nodes into ONE node per skill.
 *
 * Input: the canvas node shape every surface already uses (skillGroupKey, skill, domain,
 * testType, difficulty, prerequisites[], relatedConcepts[], mastery{status,score,...}).
 * Output: same shape (so KnowledgeMapCanvas/filters work unchanged) plus:
 *   isSkillNode, currentDok {difficulty, label, allMastered}, dokBreakdown {E,M,H → variant},
 *   variantKeys[]. Edges are collapsed to skill→skill (deduped, self-loops dropped).
 *
 * Aggregation rules (deliberate):
 * - Status precedence: STRUGGLING > IN_PROGRESS > all-MASTERED; a mix of MASTERED and
 *   UNVISITED reads as IN_PROGRESS (the skill is partially done). Raw attempt counters are
 *   NOT the basis for status — UserNodeMastery keeps internal cross-difficulty counters that
 *   can double-count across variants.
 * - currentDok: the learner's working frontier — the highest ATTEMPTED not-yet-mastered variant;
 *   nothing attempted → the lowest not-yet-mastered; all mastered → highest, allMastered=true.
 * - score: mean of the variant scores (display only).
 */
export function buildSkillGraphNodes(nodes) {
  if (!nodes?.length) return []

  // variant skillGroupKey → skill key, for remapping edges
  const variantToSkill = new Map()
  for (const n of nodes) variantToSkill.set(n.skillGroupKey, skillKeyOf(n))

  // Group variants per skill, keeping difficulty order stable
  const bySkill = new Map()
  for (const n of nodes) {
    const key = skillKeyOf(n)
    if (!bySkill.has(key)) bySkill.set(key, [])
    bySkill.get(key).push(n)
  }

  const skillNodes = []
  for (const [key, variants] of bySkill) {
    variants.sort(
      (a, b) => DIFFICULTY_ORDER.indexOf(a.difficulty) - DIFFICULTY_ORDER.indexOf(b.difficulty)
    )
    const first = variants[0]

    const dokBreakdown = {}
    for (const v of variants) {
      dokBreakdown[v.difficulty] = {
        skillGroupKey: v.skillGroupKey,
        difficulty: v.difficulty,
        dokLabel: DOK_BY_DIFFICULTY[v.difficulty],
        mastery: v.mastery || { status: 'UNVISITED', score: 0, totalAttempts: 0 },
        aggregate: v.aggregate,
        knowledgeIds: v.knowledgeIds,
        hasTutorPractice: !!v.hasTutorPractice,
        tutorPracticeId: v.tutorPracticeId ?? null
      }
    }

    const statuses = variants.map((v) => v.mastery?.status || 'UNVISITED')
    let status
    if (statuses.includes('STRUGGLING')) status = 'STRUGGLING'
    else if (statuses.includes('IN_PROGRESS')) status = 'IN_PROGRESS'
    else if (statuses.every((s) => s === 'MASTERED')) status = 'MASTERED'
    else if (statuses.includes('MASTERED')) status = 'IN_PROGRESS'
    else status = 'UNVISITED'

    // Working frontier: the variant the learner is actually at. Difficulties aren't necessarily
    // attempted in E->M->H order, so prefer the HIGHEST-difficulty variant that has been attempted
    // (IN_PROGRESS/STRUGGLING) but not mastered; if nothing is attempted yet, fall back to the lowest
    // not-yet-mastered variant (the natural next step). Variants are sorted E->M->H.
    const notMastered = variants.filter((v) => (v.mastery?.status || 'UNVISITED') !== 'MASTERED')
    const attempted = notMastered.filter((v) => (v.mastery?.status || 'UNVISITED') !== 'UNVISITED')
    const allMastered = notMastered.length === 0
    const currentVariant = attempted.length
      ? attempted[attempted.length - 1]
      : notMastered[0] || variants[variants.length - 1]
    const currentDok = {
      difficulty: currentVariant.difficulty,
      label: DOK_BY_DIFFICULTY[currentVariant.difficulty],
      allMastered
    }

    const score =
      variants.reduce((sum, v) => sum + (v.mastery?.score || 0), 0) / (variants.length || 1)
    const totalAttempts = variants.reduce((s, v) => s + (v.mastery?.totalAttempts || 0), 0)
    const correctAttempts = variants.reduce((s, v) => s + (v.mastery?.correctAttempts || 0), 0)

    // Collapse edges to skill granularity, dropping self-loops and duplicates.
    const collapse = (field) => {
      const out = new Set()
      for (const v of variants) {
        for (const ref of v[field] || []) {
          const target = variantToSkill.get(ref)
          if (target && target !== key) out.add(target)
        }
      }
      return [...out]
    }

    const tutorVariant =
      [currentVariant, ...variants].find((v) => v.hasTutorPractice && v.tutorPracticeId) || null

    skillNodes.push({
      skillGroupKey: key,
      isSkillNode: true,
      testType: first.testType || first.test_type,
      domain: first.domain,
      skill: first.skill,
      prerequisites: collapse('prerequisites'),
      relatedConcepts: collapse('relatedConcepts'),
      mastery: { status, score, totalAttempts, correctAttempts },
      currentDok,
      dokBreakdown,
      variantKeys: variants.map((v) => v.skillGroupKey),
      hasTutorPractice: variants.some((v) => v.hasTutorPractice),
      tutorPracticeId: tutorVariant?.tutorPracticeId ?? null
    })
  }

  return skillNodes
}

/**
 * Rule-based personalized message for a skill node ("needs attention", progress, readiness).
 * Kept as a single seam so the planned AI advisor can substitute richer text later.
 *
 * @param {object} skillNode - a node from buildSkillGraphNodes
 * @param {object} [opts]
 * @param {'student'|'educator'} [opts.audience]
 * @param {Array} [opts.allNodes] - all skill nodes, for prerequisite-readiness wording
 * @returns {{ tone: 'attention'|'progress'|'success'|'info', text: string }}
 */
export function getSkillAttentionMessage(skillNode, { audience = 'student', allNodes } = {}) {
  const status = skillNode.mastery?.status || 'UNVISITED'
  const dok = skillNode.currentDok?.label || 'DOK 1'
  const current = skillNode.dokBreakdown?.[skillNode.currentDok?.difficulty]

  if (audience === 'educator') {
    // Cohort wording from the worst variant's aggregate distribution when available.
    let struggling = 0
    let learners = 0
    let worstDok = dok
    for (const v of Object.values(skillNode.dokBreakdown || {})) {
      const dist = v.aggregate?.statusDistribution
      if (dist) {
        learners = Math.max(learners, v.aggregate.learnerCount || 0)
        const s = dist.STRUGGLING || 0
        if (s > struggling) {
          struggling = s
          worstDok = v.dokLabel
        }
      }
    }
    if (struggling > 0) {
      return {
        tone: 'attention',
        text: `${struggling} of ${learners} students are struggling with this skill — worst at ${worstDok}. Consider a tutor practice.`
      }
    }
    if (status === 'STRUGGLING') {
      return {
        tone: 'attention',
        text: `The class is struggling with this skill at ${dok}. Consider a tutor practice.`
      }
    }
    if (status === 'MASTERED') {
      return { tone: 'success', text: 'The class has mastered this skill across all DOK levels.' }
    }
    if (status === 'UNVISITED') {
      return { tone: 'info', text: 'No student activity on this skill yet.' }
    }
    return { tone: 'progress', text: `The class is progressing through this skill at ${dok}.` }
  }

  // Student wording
  if (status === 'STRUGGLING') {
    // Point at the variant that is actually struggling (the lowest such DOK), which need not be the
    // working frontier - reporting the frontier's score here would name the wrong DOK and percentage.
    const strugglingVariant =
      Object.values(skillNode.dokBreakdown || {}).find((v) => v.mastery?.status === 'STRUGGLING') ||
      current
    const pct = Math.round((strugglingVariant?.mastery?.score || 0) * 100)
    const strugglingDok = strugglingVariant?.dokLabel || dok
    return {
      tone: 'attention',
      text: `This skill needs attention — proficiency is ${pct}% at ${strugglingDok}. A focused practice can turn it around.`
    }
  }
  if (status === 'MASTERED') {
    return { tone: 'success', text: 'Fully mastered — every DOK level of this skill is complete.' }
  }
  if (status === 'IN_PROGRESS') {
    const vStatus = current?.mastery?.status || 'UNVISITED'
    if (vStatus === 'UNVISITED') {
      // e.g. DOK 1 mastered, DOK 2 untouched
      return { tone: 'progress', text: `Nice work so far — you're ready to start ${dok}.` }
    }
    const { remaining } = getRemainingToMaster(
      current?.mastery?.score || 0,
      skillNode.currentDok?.difficulty || 'M',
      vStatus
    )
    if (remaining > 0) {
      return {
        tone: 'progress',
        text: `${remaining} more correct answer${remaining === 1 ? '' : 's'} at ${dok} to level up. Keep going!`
      }
    }
    return { tone: 'progress', text: `You're making progress at ${dok}. Keep going!` }
  }
  // UNVISITED: prerequisite readiness when we can check it
  if (allNodes?.length && skillNode.prerequisites?.length) {
    const byKey = new Map(allNodes.map((n) => [n.skillGroupKey, n]))
    const blocking = skillNode.prerequisites
      .map((k) => byKey.get(k))
      .find((p) => p && p.mastery?.status !== 'MASTERED')
    if (blocking) {
      return {
        tone: 'info',
        text: `Master "${blocking.skill}" first — it unlocks this skill.`
      }
    }
  }
  return { tone: 'info', text: `You're ready to start this skill at ${dok}.` }
}

/**
 * Build graph layout using ELK engine.
 * Returns a promise — callers must await.
 *
 * @param {Array} nodes - Raw knowledge nodes with prerequisites, mastery, etc.
 * @returns {Promise<{ flowNodes: Array, flowEdges: Array, groupNodes: Array }>}
 */
export async function buildGraphLayout(nodes, { groupBy = 'domain' } = {}) {
  if (!nodes.length) {
    return { flowNodes: [], flowEdges: [], groupNodes: [] }
  }

  // 'hierarchical' is a prerequisite-chain view; like the admin graph's layoutHierarchical it lays
  // out within domain groups (ELK's DOWN direction already threads prerequisite edges into a chain).
  const elkGroupBy = groupBy === 'hierarchical' ? 'domain' : groupBy

  // Skill-level nodes (one per skill) use their own visual and need no E/M/H compound grouping.
  const isSkillGraph = nodes.some((n) => n.isSkillNode)
  const nodeType = isSkillGraph ? 'skillNode' : 'masteryNode'

  // Prepare nodes with _nodeType for ELK layout to pass through.
  // Map testType (camelCase from API) to test_type (snake_case expected by elkLayout.js).
  const elkNodes = nodes.map((node) => ({
    ...node,
    id: node.skillGroupKey,
    test_type: node.testType || node.test_type,
    _nodeType: nodeType
  }))

  // Build prerequisite edges
  const prereqEdges = []
  for (const node of nodes) {
    for (const prereq of node.prerequisites || []) {
      if (nodes.some((n) => n.skillGroupKey === prereq)) {
        prereqEdges.push({
          id: `${prereq}->${node.skillGroupKey}`,
          source: prereq,
          target: node.skillGroupKey,
          type: 'prerequisite'
        })
      }
    }
  }

  // Build related concept edges (not used for layout, only for display when toggled)
  const relatedEdges = []
  const seenRelatedPairs = new Set()
  for (const node of nodes) {
    for (const related of node.relatedConcepts || []) {
      if (nodes.some((n) => n.skillGroupKey === related)) {
        // Avoid duplicate edges (A->B and B->A)
        const pairKey = [node.skillGroupKey, related].sort().join('::')
        if (!seenRelatedPairs.has(pairKey)) {
          seenRelatedPairs.add(pairKey)
          relatedEdges.push({
            id: `related::${node.skillGroupKey}::${related}`,
            source: node.skillGroupKey,
            target: related,
            type: 'related'
          })
        }
      }
    }
  }

  const allEdges = [...prereqEdges, ...relatedEdges]

  const { positionedNodes, routedEdges, groupNodes } = await buildELKLayout(elkNodes, allEdges, {
    groupBy: elkGroupBy,
    direction: 'DOWN',
    nodeWidth: NODE_WIDTH,
    nodeHeight: isSkillGraph ? SKILL_NODE_HEIGHT : NODE_HEIGHT,
    // One node per skill needs no E/M/H compound clusters (they'd be single-child boxes).
    compoundSkillGroups: !isSkillGraph
  })

  // Compute mastery stats per group and enrich group node data
  const enrichedGroupNodes = groupNodes.map((gNode) => {
    // Find child nodes that belong to this group
    const childNodes = positionedNodes.filter((n) => n.parentNode === gNode.id)
    const total = childNodes.length
    const mastered = childNodes.filter((n) => n.data?.mastery?.status === 'MASTERED').length
    const inProgress = childNodes.filter((n) => n.data?.mastery?.status === 'IN_PROGRESS').length
    const struggling = childNodes.filter((n) => n.data?.mastery?.status === 'STRUGGLING').length
    const unvisited = total - mastered - inProgress - struggling

    return {
      ...gNode,
      data: {
        ...gNode.data,
        masteryStats: { total, mastered, inProgress, struggling, unvisited }
      }
    }
  })

  // Map positioned nodes to flow format
  const flowNodes = positionedNodes.map((node) => ({
    id: node.id,
    type: node.data?.isSkillNode ? 'skillNode' : 'masteryNode',
    position: node.position,
    data: node.data
  }))

  // Map routed edges to flow format, differentiating prerequisite from related
  const flowEdges = routedEdges.map((edge) => {
    const isRelated = edge.id?.startsWith('related::') || edge.data?.type === 'related'
    if (isRelated) {
      return {
        ...edge,
        type: 'routedEdge',
        data: { ...edge.data, edgeType: 'related' },
        style: { stroke: '#93C5FD', strokeWidth: 1, strokeDasharray: '5 3', opacity: 0.3 }
      }
    }
    return {
      ...edge,
      type: 'routedEdge',
      style: { stroke: '#94A3B8', strokeWidth: 2 },
      markerEnd: { type: 'arrowclosed', color: '#94A3B8' }
    }
  })

  return { flowNodes, flowEdges, groupNodes: enrichedGroupNodes }
}
